import React, { useState } from "react";
import moment from "moment";
import weeklyQuestionsIcon from "../../../utilities/images/study_dashboard/weekly_questions_icon.svg";
import weeklyAccuracyIcon from "../../../utilities/images/study_dashboard/weekly_accuracy_icon.svg";
import weeklyXpIcon from "../../../utilities/images/study_dashboard/weekly_xp_icon.svg";
import weeklyQuestionsEmptyIcon from "../../../utilities/images/study_dashboard/empty_states/weekly_questions_empty.svg";
import weeklyAccuracyEmptyIcon from "../../../utilities/images/study_dashboard/empty_states/weekly_accuracy_empty.svg";
import weeklyXpEmptyIcon from "../../../utilities/images/study_dashboard/empty_states/weekly_xp_empty.svg";
import weeklyInfoIcon from "../../../utilities/images/study_dashboard/weekly_info_icon.svg";
import WeeklyActivityInfoModal from "./WeeklyActivityInfoModal";
import "./WeeklyActivity.css";

const STAT_CONFIG = [
  {
    key: "questions",
    label: "Questions Attempted",
    emptyLabel: "No Questions Attempted",
    icon: weeklyQuestionsIcon,
    emptyIcon: weeklyQuestionsEmptyIcon,
    iconClassName: "weekly-activity__stat-icon weekly-activity__stat-icon--questions",
    getValue: (weekly) =>
      weekly?.total_attempts ??
      weekly?.questions_attempted ??
      weekly?.total_attempt ??
      weekly?.attempted ??
      null,
  },
  {
    key: "accuracy",
    label: "Accuracy",
    emptyLabel: "No Accuracy Data",
    icon: weeklyAccuracyIcon,
    emptyIcon: weeklyAccuracyEmptyIcon,
    iconClassName: "weekly-activity__stat-icon weekly-activity__stat-icon--accuracy",
    getValue: (weekly) => {
      const raw =
        weekly?.accuracy ??
        weekly?.accuracy_percent ??
        weekly?.progress_percent;
      if (raw === undefined || raw === null || raw === "") return null;
      const text = String(raw);
      return text.includes("%") ? text : `${text}%`;
    },
  },
  {
    key: "xp",
    label: "XP Points",
    emptyLabel: "No XP points yet",
    icon: weeklyXpIcon,
    emptyIcon: weeklyXpEmptyIcon,
    iconClassName: "weekly-activity__stat-icon weekly-activity__stat-icon--xp",
    getValue: (weekly) =>
      weekly?.xp_points ??
      weekly?.["XP Points"] ??
      weekly?.points ??
      null,
  },
];

const isEmptyMetric = (value) => {
  if (value === undefined || value === null || value === "" || value === "—") {
    return true;
  }
  const numeric = Number(String(value).replace("%", "").trim());
  return Number.isFinite(numeric) && numeric === 0;
};

const formatDateRange = (weekly) => {
  const range =
    weekly?.week_date ??
    weekly?.date_range ??
    weekly?.week_range ??
    weekly?.range_label ??
    weekly?.week_label;
  if (range) return range;

  const start = weekly?.week_start ?? weekly?.start_date;
  const end = weekly?.week_end ?? weekly?.end_date;
  if (start && end) {
    const startDate = moment(start);
    const endDate = moment(end);
    if (startDate.isValid() && endDate.isValid()) {
      return `${startDate.format("MMM D")} to ${endDate.format("MMM D")}`;
    }
  }

  const weekStart = moment().startOf("isoWeek");
  const weekEnd = moment().endOf("isoWeek");
  return `${weekStart.format("MMM D")} to ${weekEnd.format("MMM D")}`;
};

const WeeklyActivity = ({ weekly = {} }) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const dateRange = formatDateRange(weekly);
  const questionsValue = STAT_CONFIG[0].getValue(weekly);
  const isEmptyWeek = isEmptyMetric(questionsValue);

  return (
    <section
      className={`weekly-activity${isEmptyWeek ? " weekly-activity--empty" : ""}`}
      aria-label="Weekly activity"
    >
      <header className="weekly-activity__header">
        <div className="weekly-activity__title-row">
          <h2 className="weekly-activity__title">Weekly Activity</h2>
          <button
            type="button"
            className="weekly-activity__info-btn"
            aria-label="Weekly activity breakdown info"
            aria-expanded={isInfoModalOpen}
            onClick={() => setIsInfoModalOpen(true)}
          >
            <img src={weeklyInfoIcon} alt="" className="weekly-activity__info-icon" aria-hidden="true" />
          </button>
        </div>
        <span className="weekly-activity__range">{dateRange}</span>
      </header>

      <div className="weekly-activity__stats">
        {STAT_CONFIG.map((stat) => {
          const value = stat.getValue(weekly);

          if (isEmptyWeek) {
            return (
              <article
                key={stat.key}
                className="weekly-activity__stat weekly-activity__stat--empty"
              >
                <img
                  src={stat.emptyIcon || stat.icon}
                  alt=""
                  className={`${stat.iconClassName} weekly-activity__stat-icon--empty`}
                  aria-hidden="true"
                />
                <p className="weekly-activity__stat-empty-label">{stat.emptyLabel}</p>
              </article>
            );
          }

          return (
            <article key={stat.key} className="weekly-activity__stat">
              <div className="weekly-activity__stat-body">
                <div className="weekly-activity__stat-top">
                  <span className="weekly-activity__stat-label">{stat.label}</span>
                  <img
                    src={stat.icon}
                    alt=""
                    className={stat.iconClassName}
                    aria-hidden="true"
                  />
                </div>
                <p className="weekly-activity__stat-value">{value ?? "—"}</p>
              </div>
            </article>
          );
        })}
      </div>

      <WeeklyActivityInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </section>
  );
};

export default WeeklyActivity;
